import { useState } from "react";
import { 
  Plug, 
  Download, 
  Webhook, 
  Zap, 
  Database, 
  MessageSquare,
  Mail,
  Phone,
  Building2,
  ChevronRight,
  ExternalLink,
  Check,
  Clock,
  Settings,
  FileSpreadsheet,
  Users,
  PhoneCall
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL}/api`;

// CRM Integration Options
const CRM_INTEGRATIONS = [
  {
    id: "salesforce",
    name: "Salesforce",
    description: "Sync leads, contacts, and call logs with Salesforce CRM",
    icon: Database,
    status: "coming_soon",
    color: "blue"
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "Connect your HubSpot CRM for seamless lead management",
    icon: Building2,
    status: "coming_soon",
    color: "orange"
  },
  {
    id: "pipedrive",
    name: "Pipedrive",
    description: "Push new leads and call activities to Pipedrive",
    icon: Database,
    status: "coming_soon",
    color: "emerald"
  },
  {
    id: "zoho",
    name: "Zoho CRM",
    description: "Integrate with Zoho CRM for complete sales automation",
    icon: Database,
    status: "coming_soon",
    color: "red"
  }
];

// Automation Integrations
const AUTOMATION_INTEGRATIONS = [
  {
    id: "zapier",
    name: "Zapier",
    description: "Connect Speakr to 5,000+ apps via Zapier automations",
    icon: Zap,
    status: "coming_soon",
    color: "orange"
  },
  {
    id: "make",
    name: "Make (Integromat)",
    description: "Build complex automation workflows with Make",
    icon: Plug,
    status: "coming_soon",
    color: "purple"
  },
  {
    id: "n8n",
    name: "n8n",
    description: "Self-hosted workflow automation for advanced users",
    icon: Settings,
    status: "coming_soon",
    color: "emerald"
  }
];

// Communication Integrations
const COMM_INTEGRATIONS = [
  {
    id: "slack",
    name: "Slack",
    description: "Get call notifications and lead alerts in Slack channels",
    icon: MessageSquare,
    status: "coming_soon",
    color: "purple"
  },
  {
    id: "email",
    name: "Email Notifications",
    description: "Receive email summaries and alerts for important events",
    icon: Mail,
    status: "active",
    color: "blue"
  },
  {
    id: "sms",
    name: "SMS Notifications",
    description: "Get SMS alerts for missed calls and new leads",
    icon: Phone,
    status: "coming_soon",
    color: "emerald"
  }
];

const STATUS_BADGES = {
  active: { label: "Active", variant: "default", className: "bg-emerald-500" },
  coming_soon: { label: "Coming Soon", variant: "secondary", className: "" },
  beta: { label: "Beta", variant: "outline", className: "border-amber-500 text-amber-500" }
};

function IntegrationCard({ integration, onConfigure }) {
  const Icon = integration.icon;
  const status = STATUS_BADGES[integration.status];

  return (
    <Card className="hover:border-primary/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${integration.color}-500/10`}>
            <Icon className={`w-6 h-6 text-${integration.color}-400`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold">{integration.name}</h3>
              <Badge variant={status.variant} className={status.className}>
                {status.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{integration.description}</p>
          </div>
          {integration.status === "active" ? (
            <Button variant="outline" size="sm" onClick={() => onConfigure(integration)}>
              Configure
            </Button>
          ) : (
            <Button variant="ghost" size="sm" disabled>
              <Clock className="w-4 h-4 mr-1" />
              Soon
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Integrations() {
  const { getAuthHeader } = useAuth();
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportCSV = async (type) => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `speakr_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleSaveWebhook = async () => {
    try {
      await axios.post(`${API}/webhooks/configure`, {
        url: webhookUrl,
        enabled: webhookEnabled,
        events: ["lead.created", "call.completed"]
      }, { headers: getAuthHeader() });
      
      toast.success("Webhook configuration saved");
      setWebhookDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save webhook configuration");
    }
  };

  return (
    <div className="p-8" data-testid="integrations-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
          CRM & Integrations
        </h1>
        <p className="text-muted-foreground">
          Connect Speakr with your favorite tools and export your data
        </p>
      </div>

      <Tabs defaultValue="integrations" className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="integrations" className="gap-2">
            <Plug className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="export" className="gap-2">
            <Download className="w-4 h-4" />
            Export Data
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="gap-2">
            <Webhook className="w-4 h-4" />
            Webhooks
          </TabsTrigger>
        </TabsList>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          {/* CRM Section */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              CRM Integrations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CRM_INTEGRATIONS.map((integration) => (
                <IntegrationCard 
                  key={integration.id} 
                  integration={integration}
                  onConfigure={() => {}}
                />
              ))}
            </div>
          </div>

          {/* Automation Section */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Automation Platforms
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {AUTOMATION_INTEGRATIONS.map((integration) => (
                <IntegrationCard 
                  key={integration.id} 
                  integration={integration}
                  onConfigure={() => {}}
                />
              ))}
            </div>
          </div>

          {/* Communication Section */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Communication
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {COMM_INTEGRATIONS.map((integration) => (
                <IntegrationCard 
                  key={integration.id} 
                  integration={integration}
                  onConfigure={() => {}}
                />
              ))}
            </div>
          </div>

          {/* Request Integration */}
          <Card className="border-dashed">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">Need a different integration?</h3>
                <p className="text-sm text-muted-foreground">
                  Let us know what tools you'd like to connect with Speakr
                </p>
              </div>
              <Button variant="outline">
                Request Integration
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Export Your Data
              </CardTitle>
              <CardDescription>
                Download your Speakr data in CSV format for analysis or import into other tools
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Leads Export */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Leads & Contacts</h4>
                    <p className="text-sm text-muted-foreground">
                      Export all leads with contact info, status, and notes
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => handleExportCSV('leads')}
                  disabled={exportLoading}
                  data-testid="export-leads-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Calls Export */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <PhoneCall className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Call History</h4>
                    <p className="text-sm text-muted-foreground">
                      Export call logs with duration, outcome, and transcripts
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => handleExportCSV('calls')}
                  disabled={exportLoading}
                  data-testid="export-calls-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Contacts Export */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Contact Lists</h4>
                    <p className="text-sm text-muted-foreground">
                      Export contact lists for campaigns and outreach
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => handleExportCSV('contacts')}
                  disabled={exportLoading}
                  data-testid="export-contacts-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="w-5 h-5" />
                Webhook Configuration
              </CardTitle>
              <CardDescription>
                Receive real-time notifications when events happen in Speakr
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Webhook URL */}
              <div className="space-y-2">
                <Label htmlFor="webhook-url">Webhook URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="webhook-url"
                    placeholder="https://your-server.com/webhook"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    data-testid="webhook-url-input"
                  />
                  <Button onClick={handleSaveWebhook} data-testid="save-webhook-btn">
                    Save
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  We'll send POST requests to this URL when events occur
                </p>
              </div>

              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <h4 className="font-medium">Enable Webhooks</h4>
                  <p className="text-sm text-muted-foreground">
                    Toggle webhook delivery on or off
                  </p>
                </div>
                <Switch
                  checked={webhookEnabled}
                  onCheckedChange={setWebhookEnabled}
                  data-testid="webhook-toggle"
                />
              </div>

              {/* Available Events */}
              <div>
                <h4 className="font-medium mb-3">Available Events</h4>
                <div className="space-y-2">
                  {[
                    { event: "lead.created", description: "When a new lead is captured" },
                    { event: "call.completed", description: "When a call ends" },
                    { event: "call.started", description: "When a call begins" },
                    { event: "appointment.booked", description: "When an appointment is scheduled" }
                  ].map((item) => (
                    <div key={item.event} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {item.event}
                        </code>
                        <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                      </div>
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Example Payload */}
              <div>
                <h4 className="font-medium mb-3">Example Payload</h4>
                <pre className="p-4 bg-muted rounded-lg text-sm overflow-x-auto">
{`{
  "event": "lead.created",
  "timestamp": "2025-02-09T12:00:00Z",
  "data": {
    "lead_id": "lead_abc123",
    "name": "John Doe",
    "phone": "+1234567890",
    "email": "john@example.com",
    "source": "inbound_call",
    "agent_id": "agent_xyz789"
  }
}`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
